import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { OrbitMotif } from "@/components/OrbitMotif";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  ArrowRight,
  Sparkles,
  Wallet,
  Megaphone,
  HeadphonesIcon as Headphones,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function Affiliate() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    website: "",
    expected_monthly_referrals: "",
    pitch: "",
  });
  const [statusInfo, setStatusInfo] = useState(null);

  useImperativeLoad(() => {
    if (user) {
      api.get("/affiliate/me").then((r) => setStatusInfo(r.data)).catch(() => {});
    }
  }, [user]);

  const status = statusInfo?.affiliate_status || "none";

  const handleApply = async (e) => {
    e?.preventDefault?.();
    if (!user) {
      navigate("/auth?mode=signup&next=/affiliate");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        company_name: form.company_name || null,
        website: form.website || null,
        expected_monthly_referrals: form.expected_monthly_referrals
          ? parseInt(form.expected_monthly_referrals, 10)
          : null,
        pitch: form.pitch || null,
      };
      await api.post("/affiliate/apply", body);
      toast.success(t("affiliate_page.application_submitted"));
      const r = await api.get("/affiliate/me");
      setStatusInfo(r.data);
      await refresh();
      setShowForm(false);
    } catch (err) {
      const code = extractErrorCode(err);
      const translated = t(`affiliate_page.err_${code}`);
      toast.error(
        typeof translated === "string" && !translated.startsWith("affiliate_page.")
          ? translated
          : `Error: ${code}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAFAF8]">
        <OrbitMotif
          className="absolute -right-40 -top-40 h-[900px] w-[900px] orbit-anim"
          color="#9ACD32"
          opacity={0.12}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0B0E5C]/15 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#0B0E5C]">
              <Sparkles className="h-3 w-3 text-[#9ACD32]" />
              {t("affiliate_page.hero_pretitle")}
            </div>

            <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter text-[#05073B] sm:text-6xl lg:text-7xl">
              {t("affiliate_page.hero_title")}
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              {t("affiliate_page.hero_subtitle")}
            </p>

            <div className="mt-10">
              {status === "approved" ? (
                <Button
                  asChild
                  size="lg"
                  className="lime-glow rounded-full bg-[#9ACD32] px-8 py-6 text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
                  data-testid="affiliate-cta-dashboard"
                >
                  <Link to="/partner">
                    {t("affiliate_page.cta_dashboard")}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              ) : status === "pending" ? (
                <Card className="inline-flex max-w-md items-center gap-3 border-[#9ACD32]/30 bg-[#9ACD32]/10 p-4">
                  <Clock className="h-5 w-5 text-[#7AAA20]" />
                  <div>
                    <p className="font-semibold text-[#05073B]">
                      {t("affiliate_page.status_pending_title")}
                    </p>
                    <p className="text-xs text-slate-600">
                      {t("affiliate_page.status_pending_desc")}
                    </p>
                  </div>
                </Card>
              ) : status === "rejected" ? (
                <Card className="inline-flex max-w-md items-center gap-3 border-red-200 bg-red-50 p-4">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-semibold text-[#05073B]">
                      {t("affiliate_page.status_rejected_title")}
                    </p>
                    <p className="text-xs text-slate-600">
                      {t("affiliate_page.status_rejected_desc")}
                    </p>
                  </div>
                </Card>
              ) : (
                <Button
                  size="lg"
                  className="lime-glow rounded-full bg-[#9ACD32] px-8 py-6 text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
                  onClick={() => {
                    if (!user) navigate("/auth?mode=signup&next=/affiliate");
                    else setShowForm(true);
                  }}
                  data-testid="affiliate-cta-apply"
                >
                  {t("affiliate_page.cta_apply")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 md:grid-cols-3">
            <BenefitCard
              icon={Wallet}
              title={t("affiliate_page.benefit_commission_title")}
              desc={t("affiliate_page.benefit_commission_desc")}
              accent="lime"
              testId="benefit-commission"
            />
            <BenefitCard
              icon={Megaphone}
              title={t("affiliate_page.benefit_tools_title")}
              desc={t("affiliate_page.benefit_tools_desc")}
              testId="benefit-tools"
            />
            <BenefitCard
              icon={Headphones}
              title={t("affiliate_page.benefit_priority_title")}
              desc={t("affiliate_page.benefit_priority_desc")}
              testId="benefit-priority"
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#FAFAF8] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="text-center font-display text-4xl font-extrabold tracking-tight text-[#05073B] sm:text-5xl">
            {t("affiliate_page.how_title")}
          </h2>
          <div className="mt-16 grid gap-8 md:grid-cols-3">
            <Step
              title={t("affiliate_page.how_step1_title")}
              desc={t("affiliate_page.how_step1_desc")}
            />
            <Step
              title={t("affiliate_page.how_step2_title")}
              desc={t("affiliate_page.how_step2_desc")}
            />
            <Step
              title={t("affiliate_page.how_step3_title")}
              desc={t("affiliate_page.how_step3_desc")}
            />
          </div>
        </div>
      </section>

      {/* APPLICATION FORM MODAL */}
      {showForm && status === "none" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowForm(false)}
        >
          <Card
            className="w-full max-w-lg border-slate-200 bg-white p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="affiliate-form-modal"
          >
            <h3 className="font-display text-2xl font-bold text-[#05073B]">
              {t("affiliate_page.form_title")}
            </h3>
            <form onSubmit={handleApply} className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  {t("affiliate_page.form_company")}
                </Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="mt-1.5 h-10 rounded-lg"
                  data-testid="affiliate-form-company"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  {t("affiliate_page.form_website")}
                </Label>
                <Input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="mt-1.5 h-10 rounded-lg"
                  placeholder="https://..."
                  data-testid="affiliate-form-website"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  {t("affiliate_page.form_volume")}
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={form.expected_monthly_referrals}
                  onChange={(e) =>
                    setForm({ ...form, expected_monthly_referrals: e.target.value })
                  }
                  className="mt-1.5 h-10 rounded-lg"
                  placeholder="10"
                  data-testid="affiliate-form-volume"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  {t("affiliate_page.form_pitch")}
                </Label>
                <Textarea
                  rows={3}
                  value={form.pitch}
                  onChange={(e) => setForm({ ...form, pitch: e.target.value })}
                  className="mt-1.5 rounded-lg"
                  data-testid="affiliate-form-pitch"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setShowForm(false)}
                  data-testid="affiliate-form-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="lime-glow flex-1 rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                  data-testid="affiliate-form-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("affiliate_page.form_submit")
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
}

function BenefitCard({ icon: Icon, title, desc, accent, testId }) {
  const isLime = accent === "lime";
  return (
    <Card className="border-slate-200 p-8" data-testid={testId}>
      <div
        className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl ${
          isLime ? "bg-[#9ACD32]/15 text-[#7AAA20]" : "bg-[#0B0E5C]/8 text-[#0B0E5C]"
        }`}
      >
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="font-display text-xl font-bold text-[#05073B]">{title}</h3>
      <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{desc}</p>
    </Card>
  );
}

function Step({ title, desc }) {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#9ACD32]/15 font-display text-2xl font-extrabold text-[#7AAA20]">
        {title.charAt(0)}
      </div>
      <h3 className="font-display text-xl font-bold text-[#05073B]">{title.slice(3)}</h3>
      <p className="mx-auto mt-3 max-w-xs text-sm text-slate-600">{desc}</p>
    </div>
  );
}
